<template>
  <div>
    <FileList
      v-if="fileList"
      :disabled="
        disabled || (showFiles && !multiple && localFiles.length === 1)
      "
      :file-count="localFiles.length"
      :label="label"
      :loading="$loading.isLoading(`uploadFile_${id}`)"
      :noCard="noCard"
      :readonly="
        readonly || (showFiles && !multiple && localFiles.length === 1)
      "
      :select-options="{ multiple, accept, confirmMsg }"
      @select="uploadFile($event)">
      <template #files>
        <div v-if="showFiles">
          <div
            v-if="localFiles.length"
            :class="{ 'pl-0': readonly }"
            class="files">
            <FileItem
              v-for="file in localFiles"
              :key="file.path"
              :disabled="disabled || readonly"
              :file="file"
              @remove-file="removeFile($event)" />
          </div>
          <ContentNotFound v-else-if="showEmpty" />
        </div>
      </template>
      <template #append-action>
        <slot v-if="$slots['append-action']" name="append-action"></slot>
      </template>
    </FileList>
    <div v-if="!dropper && !fileList" class="label">
      <v-btn-group
        v-if="!readonly"
        density="compact"
        rounded="lg"
        variant="outlined">
        <v-btn
          :disabled="
            disabled || (showFiles && !multiple && localFiles.length === 1)
          "
          :loading="$loading.isLoading(`uploadFile_${id}`)"
          color="primary"
          density="comfortable"
          rounded="lg"
          size="x-small"
          @click="
            () => selectFile({ accept, multiple, confirmMsg }).then(uploadFile)
          ">
          <v-icon class="mr-1" icon="mdi-paperclip" size="large" />
          <span v-if="label">{{ label }}</span>
        </v-btn>
      </v-btn-group>
    </div>

    <FileDropper
      v-if="
        dropper &&
        !disabled &&
        !readonly &&
        !fileList &&
        (multiple ? true : !modelValue?.length)
      "
      :dropper-variant="dropperVariant"
      :loading="$loading.isLoading(`uploadFile_${id}`)"
      :outline="outline"
      :select-options="{ multiple, accept, confirmMsg }"
      @select="uploadFile($event)" />

    <div
      v-if="showFiles && !fileList"
      :class="{
        'files-wrapper': localFiles.length || showEmpty,
      }">
      <ul v-if="localFiles.length" :class="{ 'pl-0': readonly }" class="files">
        <FileItem
          v-for="file in localFiles"
          :key="file.path"
          :disabled="disabled || readonly"
          :file="file"
          @remove-file="removeFile($event)" />
      </ul>
      <ContentNotFound v-else-if="showEmpty" />
    </div>

    <ValidationError :v$="v$" />
  </div>
</template>

<script lang="ts">
import FileItem from '@/components/filesUploader/FileItem.vue'
import FileDropper from '@/components/filesUploader/FileDropper.vue'
import ValidationError from '@/components/ValidationError.vue'
import { computed, PropType, ref, watch } from 'vue'
import { generateId } from '@/utils/helpers.js'
import { selectFile } from './selectFile'
import { DropperVariant } from './FileDropper.vue'
import { FileAccept } from './selectFile/types'
import { MainFolder, NameFolder, uploadFiles as upload } from './uploadFiles'
import { handleAsync } from 'best-modules/plugins'
import { urlDeleteFile } from '@/utils/urls.js'
import moment from 'moment'
import axios from '@/plugins/axios/index.js'
import FileList from '@/components/filesUploader/FileList.vue'
import ContentNotFound from '@/components/ContentNotFound.vue'
import { useStore } from 'vuex'

export default {
  components: {
    ContentNotFound,
    FileList,
    FileDropper,
    FileItem,
    ValidationError,
  },
  emits: ['update:modelValue', 'change'],
  props: {
    modelValue: null, //setting another value against null only for type 'files'
    dropper: { type: Boolean, default: false },
    dropperVariant: {
      type: String as PropType<DropperVariant>,
      default: 'default',
    },
    outline: { type: Boolean, default: false },
    type: {
      type: String as PropType<'paths' | 'files'>,
      default: 'files',
      validator: val => ['paths', 'files'].includes(val as string),
    },
    mainFolder: {
      type: String as PropType<MainFolder>,
      required: true,
      validator: val =>
        [
          'Документы',
          'Приказы',
          'Пользователи',
          'Корреспонденция',
          'Протоколи',
          'Аудит',
          'План',
        ].includes(val as string),
    },
    nameFolder: {
      type: String as PropType<NameFolder>,
      required: true,
    },
    prefixFolder: {
      type: String as PropType<string | undefined>,
    },
    multiple: { type: Boolean, default: true },
    fileList: { type: Boolean, default: false },
    label: { type: [String, Boolean] },
    tooltipLabel: { type: [String, Boolean], default: 'Завантажити документи' },
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    noCard: { type: Boolean },
    accept: {
      type: Array as PropType<FileAccept[]>,
      default: () => [
        '.png',
        '.jpg',
        '.jpeg',
        '.svg',
        '.doc',
        '.docx',
        '.pdf',
        '.xls',
        '.xlsx',
        '.rar',
        '.zip',
      ],
    },
    showFiles: { type: Boolean, default: true },
    showEmpty: { type: Boolean },
    hideDetails: { type: Boolean },
    v$: {
      type: Object,
      default: () => ({ $validate: () => false }),
    },
    confirmMsg: { type: [String, Boolean], default: false },
  },
  setup(props, { emit }) {
    const id = generateId()
    const localFiles = ref<any[]>([])
    const typePaths = props.type === 'paths'
    const store = useStore()
    const user = computed(() => store.state.user.userData)

    watch(
      () => props.modelValue,
      files => {
        if (!files) {
          return localFiles.value.splice(0)
        }
        if (!typePaths) {
          localFiles.value.splice(0)
          localFiles.value.push(...(props.modelValue || []))
        } else {
          localFiles.value.splice(0)
          const paths = (files || [])
            .flat()
            .filter((p: any) => p && typeof p === 'string')
          localFiles.value.push(
            ...paths.map((path: any) => ({
              path,
              name: path.split('/').pop(),
            }))
          )
        }
      },
      { immediate: true }
    )

    const removeFile = (file: any) => {
      return handleAsync(`deleteFile__${file.path}`, () => {
        return axios
          .post(urlDeleteFile(), {
            file: file.path,
          })
          .then(() => {
            const emitedVal = props.modelValue.filter(
              (f: any) => (typePaths ? f : f.path) !== file.path
            )

            localFiles.value = localFiles.value.filter(
              f => f.path !== file.path
            )
            emit('update:modelValue', emitedVal)
            emit('change', emitedVal)
          })
      })
    }

    const uploadFile = async (event: any) => {
      // add loading item to files list
      localFiles.value.push({ loading: true })
      !props.multiple && localFiles.value.splice(0)

      return handleAsync(`uploadFile_${id}`, () => {
        return upload({
          files: event.blob,
          mainFolder: props.mainFolder,
          nameFolder: props.nameFolder,
          prefixFolder: props.prefixFolder,
        }).then(uploadedFiles => {
          const now = moment().format('YYYY-MM-DD HH:mm:ss')
          const filesWithDate = uploadedFiles.files.map(f => ({
            ...f,
            createdAt: now,
            user: {
              id: user.value.id,
              name: user.value.name,
              surname: user.value.surname,
              patronymic: user.value.patronymic,
            },
          }))
          localFiles.value.unshift(...filesWithDate)

          const emitedVal = [
            ...(props.modelValue || []),
            ...(props.type === 'files'
              ? filesWithDate
              : uploadedFiles[props.type]),
          ]

          emit('update:modelValue', emitedVal)
          emit('change', emitedVal)

          localFiles.value = localFiles.value.filter(f => !f.loading)
        })
      })
    }

    return {
      uploadFile,
      selectFile,
      removeFile,
      localFiles,
      id,
    }
  },
}
</script>

<style lang="scss" scoped>
.label {
  display: flex;
  align-items: center;

  & > span {
    margin-right: 0.7rem;
    font-weight: 600;
  }
}

.wrapper {
  border-radius: 4px;
  padding: 16px 8px;
  text-align: center;
  transition: 0.3s;
  display: flex;
  align-items: center;
  flex-direction: column;

  .uploader-icon {
    border-radius: 50%;
    background: #c7e2ff;
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    background: #eefafc;
  }

  .label {
    color: #1a1a1b;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: normal;
    cursor: default;
  }
}

.files-wrapper {
  border: 1px dashed rgb(212, 212, 216);
  background: white;
  border-radius: 0 0 4px 4px;
  border-top: none;
}
</style>
